/**
 * Which addresses the report viewer may open.
 *
 * The viewer puts its `url` param into an iframe under the app's own header,
 * and that param can come from a hand-made link or from the agent — untrusted
 * model output. So only an absolute http(s) address is a report:
 *  - `javascript:` and `data:` would run in the app's origin;
 *  - a relative path, or any address on the app's own origin, is not a report
 *    but the app itself — loaded in the frame it mounts a second copy of the
 *    shell and its assistant, which takes the account's room from this page.
 *
 * Pure: the origin is passed in, so the agent's URL builder (a Node-checked
 * module) and the page share one rule.
 */

/** The address as an absolute http(s) URL object, or null. Relative paths are refused. */
function absoluteHttp(raw) {
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text) return null;
  try {
    const u = new URL(text);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
  } catch {
    return null;
  }
}

/**
 * The address to open, or null. `origin` is the app's own origin; an address
 * on it is refused.
 */
export function reportAddress(raw, origin) {
  const u = absoluteHttp(raw);
  if (!u) return null;
  if (origin && u.origin === origin) return null;
  return u.href;
}

/** Whether text could be a report address at all, before the app's origin is known. */
export const isReportAddress = (raw) => absoluteHttp(raw) != null;
